import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EEBrand } from './EEBrand';
import { isLive } from '../utils';
import UserDropdown from './UserDropdown';
import '../styles/dropdown-modal.css';

// Paleta de colores estable para ligas (por ID normalizado)
// Asigna colores consistentes a ligas conocidas, y un pool rotatorio para el resto.
const KNOWN_COLORS = {
  nba:           '#1D428A',
  bsn:           '#D9282C',
  lnb:           '#0F7B6C',
  nbb:           '#E0B020',
  euroleague:    '#E86B1C',
  eurocup:       '#E86B1C',
  acb:           '#8A4FCE',
  'pro a':       '#2E5AAC',
  'pro b':       '#6C3FC2',
  lkl:           '#D43F3F',
  ncaa:          '#1976D2',
  nbl:           '#0D7F6E',
  wnba:          '#CD2E3A',
  slb:           '#4A9D4E',
};
const COLOR_POOL = ['#1D428A','#D9282C','#0F7B6C','#E0B020','#E86B1C','#8A4FCE','#2E5AAC','#6C3FC2','#D43F3F','#1976D2','#0D7F6E','#CD2E3A','#4A9D4E','#B8508D','#C77F21'];

function normLeague(liga) {
  return String(liga || 'Sin liga').trim().toLowerCase();
}

function colorFor(liga, idx) {
  const key = normLeague(liga);
  if (KNOWN_COLORS[key]) return KNOWN_COLORS[key];
  // Buscar match parcial (ej: "NBB Brasil" contiene "nbb")
  for (const k of Object.keys(KNOWN_COLORS)) {
    if (key.includes(k)) return KNOWN_COLORS[k];
  }
  return COLOR_POOL[idx % COLOR_POOL.length];
}

export default function Sidebar({ page, setPage, selectedLeague, setLeague, picks, user, userData, isAdmin, isOpen, onClose }) {
  const navigate = useNavigate();
  const footerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);

  const toggleDropdown = () => {
    if (footerRef.current) {
      setTriggerRect(footerRef.current.getBoundingClientRect());
    }
    setDropdownOpen((v) => !v);
  };
  const livePicks = picks.filter(isLive);

  // Agrupar picks en vivo por liga (con nombre original para mostrar)
  const leagueMap = {};
  livePicks.forEach(p => {
    const raw = (p.liga || 'Sin liga').trim();
    const key = raw.toLowerCase();
    if (!leagueMap[key]) {
      leagueMap[key] = { id: key, label: raw, count: 0 };
    }
    leagueMap[key].count++;
  });

  // Ordenar alfabéticamente por label
  const dynamicLeagues = Object.values(leagueMap)
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));

  const totalLive = livePicks.length;

  // Nombre/email priorizando userData (Firestore camelCase: nombre).
  // Fallback: displayName de Firebase Auth → split del email → 'Trader'.
  const displayName =
    userData?.nombre ||
    user?.displayName ||
    (user?.email ? user.email.split('@')[0] : 'Trader');
  const firstName = displayName.split(' ')[0];
  const initials = firstName.slice(0, 2).toUpperCase();
  const roleLabel = isAdmin ? 'Admin' : 'Pro · Elite';

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div className="sidebar-overlay show" onClick={onClose} />
      )}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <EEBrand size={22} />
          <button className="sidebar-close" onClick={onClose} aria-label="Cerrar menú">×</button>
        </div>

        {/* EN VIVO */}
        <div className="nav-section">
          <div className="nav-label">En vivo</div>

          {/* Todos — siempre visible */}
          <div
            className="nav-item"
            data-active={selectedLeague === 'all' && page === 'live'}
            data-league="all"
            onClick={() => { setLeague('all'); setPage('live'); }}
          >
            <div className="n-main">
              <span className="n-dot" style={{ background: 'var(--muted-2)' }} />
              <span>Todos</span>
            </div>
            {totalLive > 0 && (
              <span className="count">{totalLive}</span>
            )}
          </div>

          {/* Ligas dinámicas — solo las que tienen partidos en vivo */}
          {dynamicLeagues.length === 0 ? (
            <div style={{
              fontSize: 11.5,
              color: 'var(--muted-2)',
              padding: '10px 10px 4px',
              fontStyle: 'italic',
            }}>
              Sin partidos en vivo
            </div>
          ) : (
            dynamicLeagues.map((l, idx) => {
              const dotColor = colorFor(l.label, idx);
              return (
                <div
                  key={l.id}
                  className="nav-item"
                  data-active={selectedLeague === l.id && page === 'live'}
                  onClick={() => { setLeague(l.id); setPage('live'); }}
                >
                  <div className="n-main">
                    <span className="n-dot live-pulse" style={{ background: dotColor }} />
                    <span>{l.label}</span>
                    <span
                      className="live-mini-badge"
                      title="En vivo"
                      aria-label="En vivo"
                    >
                      <span className="live-mini-dot" />
                      <span>EN VIVO</span>
                    </span>
                  </div>
                  <span className="count">{l.count}</span>
                </div>
              );
            })
          )}
        </div>

        {/* ANÁLISIS */}
        <div className="nav-section">
          <div className="nav-label">Análisis</div>
          {isAdmin && (
            <div
              className="nav-item"
              data-active={page === 'performance'}
              onClick={() => { setPage('performance'); if (onClose) onClose(); }}
            >
              <div className="n-main">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <span>Performance Lab</span>
              </div>
            </div>
          )}
          {isAdmin && (
            <div
              className="nav-item"
              onClick={() => { navigate('/resultados-totales'); if (onClose) onClose(); }}
            >
              <div className="n-main">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <span>Resultados totales</span>
              </div>
            </div>
          )}
          {/* 2026-05-10 RT2: revertido a setPage('signals') original. El intento
              previo con <NavLink> agregaba class="active" automáticamente que
              colisionaba con data-active="false" causando ambiguidad visual. La
              navegación real ahora corre por handleNav() en el padre (Client +
              AdminDashboard ambos navigan). El overlay mobile que bloqueaba
              clicks en desktop se oculta vía media query en index.css. */}
          <div
            className="nav-item"
            data-active={page === 'signals'}
            onClick={() => { setPage('signals'); if (onClose) onClose(); }}
          >
            <div className="n-main">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
              <span>Historial de señales</span>
            </div>
          </div>
          {/* FASE V6 — Cómo funciona el motor (link a /como-funciona) */}
          <div
            className="nav-item"
            data-tour="tour-como-funciona"
            data-active={page === 'como-funciona'}
            onClick={() => { navigate('/como-funciona'); if (onClose) onClose(); }}
          >
            <div className="n-main">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              <span>Cómo funciona</span>
            </div>
          </div>
          {/* Centro de aprendizaje (tutoriales en video + guía oficial + conceptos) */}
          <div
            className="nav-item"
            data-tour="tour-aprende"
            data-active={page === 'aprende'}
            onClick={() => { navigate('/aprende'); if (onClose) onClose(); }}
          >
            <div className="n-main">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              <span>Aprende</span>
            </div>
          </div>
          {/* Oportunidades pre-partido (NBA/WNBA) — solo plan activo */}
          <div
            className="nav-item"
            data-active={page === 'oportunidades'}
            onClick={() => { navigate('/oportunidades'); if (onClose) onClose(); }}
          >
            <div className="n-main">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 17 9 11 13 15 21 7"/>
                <polyline points="14 7 21 7 21 14"/>
              </svg>
              <span>Oportunidades</span>
            </div>
          </div>
        </div>

        {/* AJUSTES — solo admin (FASE 1.C) */}
        {isAdmin && (
          <div className="nav-section">
            <div className="nav-label">Ajustes</div>
            <div
              className="nav-item"
              data-active={page === 'config'}
              onClick={() => { setPage('config'); if (onClose) onClose(); }}
            >
              <div className="n-main">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <span>Configuración</span>
              </div>
            </div>
          </div>
        )}

        {/* Punto de entrada OPCIONAL a la app (/app). Regla definitiva:
            el dashboard es el default para todos; jamás redirect a /app.
            Visible solo para admin hasta que F3 esté completa y Matías
            dé OK para mostrarlo a clientes. */}
        {isAdmin && (
          <a
            href={(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true ? '/app' : '/app/instalar'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, margin: '0 14px 10px',
              padding: '9px 12px', borderRadius: 10, background: 'rgba(255,77,13,.08)',
              border: '1px solid rgba(255,77,13,.25)', color: '#FF4D0D',
              fontSize: 13, fontWeight: 700, textDecoration: 'none',
            }}
          >
            📱 Instalar la app
          </a>
        )}

        {/* Footer (avatar + nombre) — ahora trigger del UserDropdown.
            La card "Mi plan" + botón "Cerrar sesión" se eliminaron;
            ambas opciones viven dentro del dropdown / modal Mi cuenta. */}
        <button
          ref={footerRef}
          type="button"
          data-userdropdown-trigger="true"
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          onClick={toggleDropdown}
          className={`sidebar-footer${dropdownOpen ? ' dm-trigger-active' : ''}`}
          style={{
            background: 'transparent',
            border: 0,
            width: 'auto',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          <div className="avatar">{initials}</div>
          <div className="who">
            <b>{firstName}</b>
            <span>{roleLabel}</span>
          </div>
        </button>
      </aside>

      <UserDropdown
        open={dropdownOpen}
        triggerRect={triggerRect}
        onClose={() => setDropdownOpen(false)}
      />
    </>
  );
}
